// Customer detail — lifetime value, history, and aggregate products.
// Route: /customers/:id
//
// Shows:
//   - Customer profile (name, company, phone, email, status)
//   - Lifetime stats: total spent, transactions count, last visit, products bought
//   - Piutang (receivables) panel: outstanding txns, payment recording, wa.me reminder
//   - Full transaction history (across all events) with quick view
//   - Top products purchased (aggregated)

import { useMemo } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Building2,
  Mail,
  Phone,
  User as UserIcon,
  TrendingUp,
  Calendar,
  Receipt,
  Package,
  Clock,
} from 'lucide-react';
import { useRequestsFor } from '@/hooks/useApprovals';
import { useCustomer } from '@/hooks/useCustomers';
import { useTransactionsByCustomer } from '@/hooks/useTransactions';
import { useEvents } from '@/hooks/useEvents';
import { PageHeader } from '@/components/ui/PageHeader';
import { Card, CardTitle, CardDescription } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Chip } from '@/components/ui/Chip';
import { LoadingState } from '@/components/ui/LoadingState';
import { EmptyState } from '@/components/ui/EmptyState';
import { Table, THead, TBody, TR, TH, TD } from '@/components/ui/Table';
import { formatIDR, formatNumber, formatRelative, formatDateTime } from '@/lib/format';
import { ReceivablesPanel } from '@/components/customers/ReceivablesPanel';
import type { AppTransaction, PaymentStatus } from '@/types/domain';

const STATUS_LABEL: Record<PaymentStatus, string> = {
  paid: 'Paid',
  pending_delivery: 'Pending delivery',
  pending_payment: 'Tempo',
  overdue: 'Overdue',
  partially_returned: 'Partially returned',
  returned: 'Returned',
  cancelled: 'Cancelled',
};

const STATUS_VARIANT: Record<PaymentStatus, 'success' | 'warning' | 'info' | 'danger' | 'default'> = {
  paid: 'success',
  pending_delivery: 'warning',
  pending_payment: 'info',
  overdue: 'danger',
  partially_returned: 'info',
  returned: 'default',
  cancelled: 'danger',
};

interface ProductAgg {
  productId: string;
  name: string;
  qty: number;
  subtotal: number;
}

export default function CustomerDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data: customer, isLoading: loadingCustomer } = useCustomer(id ?? null);
  const { data: txns, isLoading: loadingTxns } = useTransactionsByCustomer(id ?? null, 200);
  const { data: events } = useEvents();

  // Build event lookup map (id -> event) for inline name display.
  const eventMap = useMemo(() => {
    const m = new Map<string, string>();
    (events ?? []).forEach((e) => m.set(e.id, e.name));
    return m;
  }, [events]);

  // Lifetime aggregations.
  const stats = useMemo(() => {
    if (!txns || txns.length === 0) {
      return {
        totalSpent: 0,
        totalTransactions: 0,
        lastVisit: null as number | null,
        productAgg: [] as ProductAgg[],
      };
    }
    let totalSpent = 0;
    let totalTransactions = 0;
    let lastVisit: number | null = null;
    const productAgg = new Map<string, ProductAgg>();

    for (const tx of txns) {
      if (tx.paymentStatus === 'cancelled') continue;
      totalTransactions += 1;
      totalSpent += tx.grandTotal;
      if (lastVisit === null || tx.createdAt > lastVisit) {
        lastVisit = tx.createdAt;
      }
      for (const item of tx.items) {
        const netQty = Math.max(0, item.qty - item.returnedQty);
        if (netQty === 0) continue;
        const existing = productAgg.get(item.productId);
        const subtotal = netQty * item.price;
        if (existing) {
          existing.qty += netQty;
          existing.subtotal += subtotal;
        } else {
          productAgg.set(item.productId, {
            productId: item.productId,
            name: item.name,
            qty: netQty,
            subtotal,
          });
        }
      }
    }

    return {
      totalSpent,
      totalTransactions,
      lastVisit,
      productAgg: Array.from(productAgg.values())
        .sort((a, b) => b.subtotal - a.subtotal)
        .slice(0, 10),
    };
  }, [txns]);

  if (loadingCustomer) {
    return <LoadingState message="Memuat customer…" />;
  }

  if (!customer) {
    return (
      <>
        <PageHeader
          title="Customer tidak ditemukan"
          description="Customer ini mungkin sudah dihapus atau tidak ada di database."
        />
        <Link to="/customers">
          <Button variant="primary" leftIcon={<ArrowLeft size={16} />}>
            Kembali ke daftar customer
          </Button>
        </Link>
      </>
    );
  }

  return (
    <>
      <div className="mb-4">
        <Link
          to="/customers"
          className="inline-flex items-center gap-1 text-small text-ink-subtle hover:text-ink transition-colors"
        >
          <ArrowLeft size={14} />
          Semua customer
        </Link>
      </div>

      <PendingEditNotice customerId={customer.id} />

      <PageHeader
        overline="Customer detail"
        title={customer.name}
        description={
          customer.company
            ? `${customer.company} · ${customer.phone}`
            : customer.phone
        }
        actions={
          <Chip variant={customer.isActive ? 'success' : 'default'}>
            {customer.isActive ? 'Active' : 'Inactive'}
          </Chip>
        }
      />

      {/* === Profile + Lifetime stats === */}
      <div className="grid gap-4 lg:grid-cols-3 mb-6">
        <Card>
          <CardTitle>Profil</CardTitle>
          <CardDescription>Informasi kontak customer.</CardDescription>
          <dl className="mt-4 space-y-3 text-small">
            <ProfileRow icon={<UserIcon size={14} />} label="Nama">
              <span className="font-medium text-ink">{customer.name}</span>
            </ProfileRow>
            {customer.company && (
              <ProfileRow icon={<Building2 size={14} />} label="Perusahaan">
                <span className="text-ink">{customer.company}</span>
              </ProfileRow>
            )}
            <ProfileRow icon={<Phone size={14} />} label="Telepon">
              <code className="font-mono text-ink">{customer.phone}</code>
            </ProfileRow>
            {customer.email && (
              <ProfileRow icon={<Mail size={14} />} label="Email">
                <span className="text-ink break-all">{customer.email}</span>
              </ProfileRow>
            )}
            <ProfileRow icon={<Calendar size={14} />} label="Terdaftar">
              <span className="text-ink-subtle">{formatDateTime(customer.createdAt)}</span>
            </ProfileRow>
          </dl>
        </Card>

        <Card>
          <div className="flex items-center gap-3">
            <span className="w-10 h-10 rounded-full bg-success/10 text-success flex items-center justify-center flex-shrink-0">
              <TrendingUp size={18} />
            </span>
            <div>
              <p className="text-overline uppercase text-ink-subtle font-medium tracking-wider">
                Total belanja
              </p>
              <p className="text-metric font-display font-semibold mt-1 text-ink">
                {formatIDR(stats.totalSpent)}
              </p>
              <p className="text-caption text-ink-subtle mt-1">
                Lifetime value · {formatNumber(stats.totalTransactions)} transaksi
              </p>
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-center gap-3">
            <span className="w-10 h-10 rounded-full bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
              <Calendar size={18} />
            </span>
            <div>
              <p className="text-overline uppercase text-ink-subtle font-medium tracking-wider">
                Kunjungan terakhir
              </p>
              <p className="text-subhead font-display font-semibold mt-1 text-ink">
                {stats.lastVisit ? formatRelative(stats.lastVisit) : '—'}
              </p>
              <p className="text-caption text-ink-subtle mt-1">
                {stats.lastVisit ? formatDateTime(stats.lastVisit) : 'Belum pernah transaksi'}
              </p>
            </div>
          </div>
        </Card>
      </div>

      {/* === Piutang (receivables) — Phase 6 === */}
      <ReceivablesPanel
        customerId={customer.id}
        customerName={customer.name}
        customerPhone={customer.phone}
      />

      {/* === Top products === */}
      {stats.productAgg.length > 0 && (
        <Card padding="none" className="mb-6">
          <div className="px-5 py-4 border-b border-line flex items-center justify-between gap-3">
            <div>
              <h3 className="text-subhead font-display font-semibold text-ink">
                Produk yang pernah dibeli
              </h3>
              <p className="text-caption text-ink-subtle mt-0.5">
                Agregat dari semua transaksi customer ini
              </p>
            </div>
          </div>
          <Table>
            <THead>
              <TR>
                <TH>Produk</TH>
                <TH className="text-right">Qty</TH>
                <TH className="text-right">Subtotal</TH>
              </TR>
            </THead>
            <TBody>
              {stats.productAgg.map((p) => (
                <TR key={p.productId}>
                  <TD>
                    <div className="flex items-center gap-2">
                      <Package size={14} className="text-ink-subtle flex-shrink-0" />
                      <span className="font-medium text-ink">{p.name}</span>
                    </div>
                  </TD>
                  <TD className="text-right">
                    <span className="font-mono text-small text-ink">
                      {formatNumber(p.qty)}
                    </span>
                  </TD>
                  <TD className="text-right">
                    <span className="font-medium text-ink whitespace-nowrap">
                      {formatIDR(p.subtotal)}
                    </span>
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        </Card>
      )}

      {/* === Transaction history === */}
      <Card padding="none">
        <div className="px-5 py-4 border-b border-line flex items-center justify-between gap-3">
          <div>
            <h3 className="text-subhead font-display font-semibold text-ink">
              Riwayat transaksi
            </h3>
            <p className="text-caption text-ink-subtle mt-0.5">
              Semua transaksi customer ini di semua event
            </p>
          </div>
          <span className="chip">{formatNumber(txns?.length ?? 0)} transaksi</span>
        </div>

        {loadingTxns ? (
          <LoadingState message="Memuat transaksi…" />
        ) : !txns || txns.length === 0 ? (
          <EmptyState
            icon={<Receipt size={24} strokeWidth={1.5} />}
            title="Belum ada transaksi"
            description="Customer ini belum pernah bertransaksi."
          />
        ) : (
          <Table>
            <THead>
              <TR>
                <TH>Invoice</TH>
                <TH>Tanggal</TH>
                <TH>Event</TH>
                <TH>Items</TH>
                <TH>Status</TH>
                <TH className="text-right">Total</TH>
              </TR>
            </THead>
            <TBody>
              {txns.map((t) => (
                <TransactionRow
                  key={t.id}
                  tx={t}
                  eventName={eventMap.get(t.eventId)}
                  onClick={() => navigate(`/invoice/${t.id}`)}
                />
              ))}
            </TBody>
          </Table>
        )}
      </Card>
    </>
  );
}

function ProfileRow({
  icon,
  label,
  children,
}: {
  icon: React.ReactNode;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-start gap-2.5">
      <span className="text-ink-subtle mt-0.5 flex-shrink-0">{icon}</span>
      <div className="min-w-0 flex-1">
        <dt className="text-overline uppercase text-ink-subtle font-medium tracking-wider text-[10px]">
          {label}
        </dt>
        <dd className="mt-0.5">{children}</dd>
      </div>
    </div>
  );
}

function TransactionRow({
  tx,
  eventName,
  onClick,
}: {
  tx: AppTransaction;
  eventName: string | undefined;
  onClick: () => void;
}) {
  const itemCount = tx.items.reduce((sum, i) => sum + i.qty, 0);
  return (
    <TR className="cursor-pointer" onClick={onClick}>
      <TD>
        <span className="font-mono text-caption text-primary">{tx.invoiceNumber}</span>
      </TD>
      <TD>
        <span className="text-small text-ink whitespace-nowrap">
          {formatRelative(tx.createdAt)}
        </span>
      </TD>
      <TD>
        <span className="text-small text-ink truncate max-w-xs inline-block">
          {eventName ?? <span className="text-ink-subtle italic">Event</span>}
        </span>
      </TD>
      <TD>
        <span className="text-caption text-ink-subtle">
          {tx.items.length} item · {formatNumber(itemCount)} qty
        </span>
      </TD>
      <TD>
        <Chip variant={STATUS_VARIANT[tx.paymentStatus]}>
          {STATUS_LABEL[tx.paymentStatus]}
        </Chip>
      </TD>
      <TD className="text-right">
        <span className="font-medium text-ink whitespace-nowrap">
          {formatIDR(tx.grandTotal)}
        </span>
      </TD>
    </TR>
  );
}

const EDIT_FIELD: Record<string, string> = {
  name: 'Nama', phone: 'Telepon', company: 'Perusahaan', email: 'Email', address: 'Alamat', tax_id: 'NIK / NPWP',
};

/** A proposed edit waiting in the approval inbox (migration 0031). */
function PendingEditNotice({ customerId }: { customerId: string }) {
  const { data } = useRequestsFor('customer.edit', 'customer_id', customerId);
  const pending = data?.find((r) => r.status === 'pending');
  if (!pending) return null;
  const changes =
    pending.payload.changes && typeof pending.payload.changes === 'object'
      ? (pending.payload.changes as Record<string, { from?: unknown; to?: unknown }>)
      : {};
  return (
    <div role="status" className="mb-4 p-3 rounded-lg border border-warning/30 bg-warning/5 flex items-start gap-3 text-small">
      <Clock size={18} className="text-warning shrink-0 mt-0.5" />
      <div>
        <p className="font-semibold text-ink">Perubahan data menunggu persetujuan</p>
        <p className="text-ink-subtle mt-0.5">
          Diajukan {pending.requestedByName ?? 'staf'} · {formatDateTime(pending.createdAt)}
          {pending.reason ? ` · “${pending.reason}”` : ''}
        </p>
        <ul className="mt-1.5 space-y-0.5">
          {Object.entries(changes).map(([f, ch]) => (
            <li key={f} className="text-ink">
              <span className="text-ink-subtle">{EDIT_FIELD[f] ?? f}:</span>{' '}
              <span className="line-through text-ink-subtle">{String(ch?.from ?? '—')}</span>
              {' → '}
              <span className="font-medium">{String(ch?.to ?? '—')}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
